import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader, Tabs } from '@mantine/core';
import { IoInformationCircleOutline } from 'react-icons/io5';
import { notifications } from '@mantine/notifications';
import { Breadcrumbs, OptionsTable, type OptionsTableColumn } from '@components';
import {
  Alert,
  Button,
  CachedImage,
  DotMenu,
  Input,
  Modal,
  RefetchButton,
  Switch,
  Tooltip,
} from '@shared/ui';
import { sellerFinanceApi } from '@modules/seller-finance/api/seller-finance.api';
import type {
  AdvertisingLedgerKind,
  AdvertisingLedgerRow,
  SellerAdvertisingCredit,
} from '@modules/seller-finance/types';
import {
  useSellerPromotion,
  useUpdateSellerPromotion,
  type SellerPromotionRow,
} from '@modules/products';
import { sellerHelpUrl } from '@shared/utils/seller-help-url';
import '@shared/styles/page.css';
import './SellerPromotionPage.css';
import SellerSearchPreviewTab from './SellerSearchPreviewTab';

const PROMOTION_SETTINGS_COOLDOWN_MS = 1 * 60 * 1000;

type PromotionTableRow = SellerPromotionRow & { id: number };

const numericColStyle = {
  whiteSpace: 'nowrap' as const,
  textAlign: 'right' as const,
  fontVariantNumeric: 'tabular-nums' as const,
};

function ProductThumb({ imageUrl }: { imageUrl: string | null }) {
  return (
    <div className="seller-promotion-table__thumb">
      {imageUrl ? (
        <CachedImage src={imageUrl} alt="" loading="lazy" />
      ) : (
        <span className="seller-promotion-table__thumb-placeholder">—</span>
      )}
    </div>
  );
}

function formatRub(n: number): string {
  return n.toLocaleString('ru-RU', { maximumFractionDigits: 2 });
}

function formatCount(n: number): string {
  return n.toLocaleString('ru-RU');
}

const LEDGER_KIND_LABEL: Record<AdvertisingLedgerKind, string> = {
  TOPUP: 'Пополнение',
  SPEND: 'Списание',
  REFUND: 'Вывод',
  ADJUSTMENT: 'Корректировка',
};

function formatLedgerWhen(iso: string): string {
  return new Date(iso).toLocaleDateString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: 'Europe/Moscow',
  });
}

const ledgerHistoryColumns: OptionsTableColumn<AdvertisingLedgerRow>[] = [
  {
    key: 'occurredAt',
    label: 'Дата',
    render: (row) => formatLedgerWhen(row.occurredAt),
  },
  {
    key: 'kind',
    label: 'Операция',
    render: (row) => LEDGER_KIND_LABEL[row.kind] ?? row.kind,
  },
  {
    key: 'title',
    label: 'Описание',
    render: (row) => row.title?.trim() || '—',
  },
  {
    key: 'amountRub',
    label: 'Сумма',
    style: numericColStyle,
    render: (row) => (
      <span
        className={
          row.amountRub >= 0
            ? 'seller-promotion-ledger-amount seller-promotion-ledger-amount--plus'
            : 'seller-promotion-ledger-amount seller-promotion-ledger-amount--minus'
        }
      >
        {row.amountRub >= 0 ? '+' : ''}
        {formatRub(row.amountRub)} ₽
      </span>
    ),
  },
];

function formatWeekRange(weekStartIso: string, weekEndExclusiveIso: string): string {
  const start = new Date(weekStartIso);
  const end = new Date(new Date(weekEndExclusiveIso).getTime() - 1);
  const fmt = new Intl.DateTimeFormat('ru-RU', {
    day: 'numeric',
    month: 'short',
    timeZone: 'Europe/Moscow',
  });
  return `${fmt.format(start)} – ${fmt.format(end)}`;
}

function PromotionMetricHeader({ label, hint }: { label: string; hint: string }) {
  return (
    <span className="seller-promotion-table__metric-header">
      <span>{label}</span>
      <Tooltip
        label={hint}
        withArrow
        position="top"
        multiline
        w={240}
      >
        <button
          type="button"
          className="seller-promotion-table__metric-info"
          aria-label={`Что означает «${label}»`}
        >
          <IoInformationCircleOutline size={16} aria-hidden />
        </button>
      </Tooltip>
    </span>
  );
}

function cooldownLeftMs(changedAt: string | null): number {
  if (!changedAt) {
    return 0;
  }
  return Math.max(
    0,
    PROMOTION_SETTINGS_COOLDOWN_MS - (Date.now() - new Date(changedAt).getTime()),
  );
}

function storedCpcValue(row: SellerPromotionRow): number {
  const n = row.cpcRub != null ? Number(row.cpcRub) : 0;
  return Number.isFinite(n) && n > 0 ? n : 0;
}

function storedCpcDraft(row: SellerPromotionRow): string {
  const n = storedCpcValue(row);
  return n > 0 ? String(n) : '';
}

function usePromotionSave(row: SellerPromotionRow) {
  const updatePromotion = useUpdateSellerPromotion();
  const onCooldown = cooldownLeftMs(row.promotionChangedAt) > 0;

  const save = async (data: { cpcRub?: number; promotionEnabled?: boolean }) => {
    try {
      await updatePromotion.mutateAsync({
        id: row.productId,
        data: {
          cpcRub: data.cpcRub ?? (row.cpcRub ?? 0),
          promotionEnabled: data.promotionEnabled ?? row.promotionEnabled,
        },
      });
    } catch (e: unknown) {
      const msg =
        e && typeof e === 'object' && 'response' in e
          ? (e as { response?: { data?: { message?: string } } }).response?.data?.message
          : e instanceof Error
            ? e.message
            : 'Не удалось сохранить';
      notifications.show({
        title: 'Ошибка',
        message: String(msg ?? 'Не удалось сохранить'),
        color: 'red',
      });
    }
  };

  return { save, onCooldown, isPending: updatePromotion.isPending };
}

function PromotionToggleCell({
  row,
  inputLocked,
  lockReason,
  balanceRub,
  budgetPaused,
  weeklyRemainingRub,
}: {
  row: SellerPromotionRow;
  inputLocked: boolean;
  lockReason: string | null;
  balanceRub: number | null;
  budgetPaused: boolean;
  weeklyRemainingRub: number | null;
}) {
  const { save, onCooldown, isPending } = usePromotionSave(row);
  const hasCpc = (row.cpcRub ?? 0) > 0;
  const disabled = inputLocked || onCooldown || isPending || !hasCpc;
  const bid = row.cpcRub ?? 0;
  const uncoveredByBalance =
    balanceRub != null && row.promotionEnabled && bid > balanceRub;
  const uncoveredByWeek =
    weeklyRemainingRub != null && row.promotionEnabled && bid > weeklyRemainingRub;
  const uncovered =
    row.promotionEnabled && (budgetPaused || uncoveredByBalance || uncoveredByWeek);
  const warnLabel = budgetPaused
    ? 'Недельный бюджет кончился. Клики не списываются, в выдаче ставка не учитывается.'
    : uncoveredByWeek && uncoveredByBalance
      ? 'На ставку не хватает рекламного баланса и остатка недельного бюджета. Клик не списывается, в выдаче ставка не учитывается.'
      : uncoveredByWeek
        ? 'На ставку не хватает остатка недельного бюджета. Клик не списывается, в выдаче ставка не учитывается.'
        : 'На ставку не хватает рекламного баланса. Клик не списывается, в выдаче ставка не учитывается.';

  const hint = !hasCpc
    ? 'Сначала укажите цену клика справа'
    : inputLocked
      ? lockReason ?? 'Продвижение временно недоступно'
      : onCooldown
        ? 'Недавно меняли настройки — подождите'
        : null;

  const switchEl = (
    <span className="seller-promotion-table__switch-hit">
      <Switch
        checked={row.promotionEnabled}
        disabled={disabled}
        onChange={(e) => {
          void save({ promotionEnabled: e.currentTarget.checked });
        }}
      />
    </span>
  );

  return (
    <span
      className="seller-promotion-table__switch-wrap"
      onClick={(e) => e.stopPropagation()}
    >
      {hint ? (
        <Tooltip
          label={hint}
          withArrow
          position="top"
          openDelay={180}
          multiline
          w={220}
        >
          {switchEl}
        </Tooltip>
      ) : (
        switchEl
      )}
      {row.rankPending ? (
        <Tooltip
          label="Идёт пересчёт места в выдаче"
          withArrow
          position="top"
        >
          <span
            className="seller-promotion-table__pending"
            aria-label="Идёт пересчёт места в выдаче"
          >
            <span className="seller-promotion-table__spinner" />
          </span>
        </Tooltip>
      ) : uncovered ? (
        <Tooltip
          label={warnLabel}
          withArrow
          position="top"
          multiline
          w={240}
        >
          <span
            className="seller-promotion-balance-warn"
            aria-label={warnLabel}
          >
            !
          </span>
        </Tooltip>
      ) : null}
    </span>
  );
}

function PromotionCpcCell({
  row,
  inputLocked,
}: {
  row: SellerPromotionRow;
  inputLocked: boolean;
}) {
  const updatePromotion = useUpdateSellerPromotion();
  const [draft, setDraft] = useState(() => storedCpcDraft(row));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setDraft(storedCpcDraft(row));
  }, [row.productId, row.cpcRub]);

  const onCooldown = cooldownLeftMs(row.promotionChangedAt) > 0;

  const persist = async () => {
    const normalized = draft.trim().replace(',', '.');
    const parsed = normalized === '' ? 0 : Number(normalized);
    const nextCpc = Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
    const currentCpc = storedCpcValue(row);
    if (nextCpc === currentCpc) {
      return;
    }
    setSaving(true);
    try {
      await updatePromotion.mutateAsync({
        id: row.productId,
        data: { cpcRub: nextCpc, promotionEnabled: nextCpc > 0 },
      });
    } catch (e: unknown) {
      const msg =
        e && typeof e === 'object' && 'response' in e
          ? (e as { response?: { data?: { message?: string } } }).response?.data
            ?.message
          : e instanceof Error
            ? e.message
            : 'Не удалось сохранить';
      notifications.show({
        title: 'Ошибка',
        message: String(msg ?? 'Не удалось сохранить'),
        color: 'red',
      });
      setDraft(storedCpcDraft(row));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Input
      type="number"
      min={0}
      step={0.01}
      size="xs"
      w={96}
      className="seller-promotion-table__cpc-input"
      placeholder="Нет"
      value={draft}
      onChange={(e) => setDraft(e.currentTarget.value)}
      onClick={(e) => e.stopPropagation()}
      onBlur={() => {
        void persist();
      }}
      disabled={saving || onCooldown || inputLocked}
      styles={{
        input: { textAlign: 'right' as const },
      }}
    />
  );
}

function WeeklyBudgetCard({
  weeklyBudget,
}: {
  weeklyBudget:
  | {
    weeklyBudgetRub: number | null;
    spentRub: number;
    remainingRub: number | null;
    paused: boolean;
    weekStart: string;
    weekEndExclusive: string;
  }
  | undefined;
}) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(() =>
    weeklyBudget?.weeklyBudgetRub != null
      ? String(weeklyBudget.weeklyBudgetRub)
      : '',
  );

  useEffect(() => {
    setDraft(
      weeklyBudget?.weeklyBudgetRub != null
        ? String(weeklyBudget.weeklyBudgetRub)
        : '',
    );
  }, [weeklyBudget?.weeklyBudgetRub]);

  const mutation = useMutation({
    mutationFn: (weeklyBudgetRub: number | null) =>
      sellerFinanceApi.updateMyPromotionBudget(weeklyBudgetRub),
    onSuccess: (result) => {
      setOpen(false);
      void queryClient.invalidateQueries({
        queryKey: ['seller-advertising-credit'],
      });
      void queryClient.invalidateQueries({
        queryKey: ['products', 'seller-promotion'],
      });
      notifications.show({
        title: result.resumed ? 'Продвижение возобновлено' : 'Бюджет сохранён',
        message: result.resumed
          ? 'Товары снова в продвижении с прежними ставками.'
          : 'Недельный лимит обновлён.',
        color: 'green',
      });
    },
    onError: (e: unknown) => {
      const msg =
        e && typeof e === 'object' && 'response' in e
          ? (e as { response?: { data?: { message?: string } } }).response?.data
            ?.message
          : e instanceof Error
            ? e.message
            : 'Не удалось сохранить бюджет';
      notifications.show({
        title: 'Ошибка',
        message: String(msg ?? 'Не удалось сохранить бюджет'),
        color: 'red',
      });
    },
  });

  const save = () => {
    const normalized = draft.trim().replace(',', '.');
    if (normalized === '') {
      void mutation.mutateAsync(null);
      return;
    }
    const parsed = Number(normalized);
    if (!Number.isFinite(parsed) || parsed < 1) {
      notifications.show({
        title: 'Ошибка',
        message:
          'Укажите бюджет не меньше 1 ₽ или очистите поле, чтобы снять лимит.',
        color: 'red',
      });
      return;
    }
    void mutation.mutateAsync(parsed);
  };

  const loaded = weeklyBudget != null;
  const spent = weeklyBudget?.spentRub ?? 0;
  const budget = weeklyBudget?.weeklyBudgetRub;
  const progress =
    budget != null && budget > 0 ? Math.min(100, (spent / budget) * 100) : 0;
  const limitLabel = budget != null ? `${formatRub(budget)} ₽` : 'без лимита';
  const balanceLabel =
    !loaded
      ? '—'
      : budget != null && budget > 0
        ? `${formatRub(spent)} из ${formatRub(budget)} ₽`
        : limitLabel;

  return (
    <>
      <div className="seller-promotion-wallet">
        <div className="seller-promotion-wallet__main">
          <div>
            <p className="seller-promotion-budget__title">Бюджет на неделю</p>
            <p className="seller-promotion-wallet__balance seller-promotion-wallet__balance--ratio">
              {balanceLabel}
            </p>
          </div>
          <Button onClick={() => setOpen(true)} disabled={!loaded}>
            Изменить
          </Button>
        </div>
      </div>
      <Modal
        isOpen={open}
        onClose={() => {
          if (!mutation.isPending) setOpen(false);
        }}
        title={
          <>
            Бюджет на неделю
            {weeklyBudget ? (
              <span className="seller-promotion-budget__period">
                {' '}
                {formatWeekRange(
                  weeklyBudget.weekStart,
                  weeklyBudget.weekEndExclusive,
                )}
              </span>
            ) : null}
          </>
        }
        maxWidth={460}
      >
        <div className="seller-promotion-wallet__modal">
          <p className="seller-promotion-budget__hint seller-promotion-budget__hint--modal">
            Пн–вс (МСК). При достижении лимита продвижение останавливается, товары и
            ставки сохраняются.{' '}
            <a
              href={sellerHelpUrl('cpc-budget')}
              className="seller-promotion-budget__help-link"
              target="_blank"
              rel="noopener noreferrer"
            >
              Подробнее в «Помощи»
            </a>
          </p>
          <div className="seller-promotion-budget__stats">
            <div>
              <span className="seller-promotion-budget__stat-label">Потрачено</span>
              <span className="seller-promotion-budget__stat-value">
                {formatRub(spent)} ₽
              </span>
            </div>
            <div>
              <span className="seller-promotion-budget__stat-label">Лимит</span>
              <span className="seller-promotion-budget__stat-value">{limitLabel}</span>
            </div>
            <div>
              <span className="seller-promotion-budget__stat-label">Остаток</span>
              <span className="seller-promotion-budget__stat-value">
                {weeklyBudget?.remainingRub != null
                  ? `${formatRub(weeklyBudget.remainingRub)} ₽`
                  : '—'}
              </span>
            </div>
          </div>
          {budget != null && budget > 0 ? (
            <div
              className="seller-promotion-budget__bar"
              role="progressbar"
              aria-valuenow={Math.round(progress)}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label="Расход недельного бюджета"
            >
              <div
                className="seller-promotion-budget__bar-fill"
                style={{ width: `${progress}%` }}
              />
            </div>
          ) : null}
          <Input
            type="number"
            min={1}
            step={1}
            label={weeklyBudget?.paused ? 'Увеличить бюджет, ₽' : 'Бюджет, ₽'}
            placeholder="Без лимита"
            value={draft}
            onChange={(e) => setDraft(e.currentTarget.value)}
            disabled={mutation.isPending}
          />
          <Button onClick={save} disabled={mutation.isPending}>
            {mutation.isPending
              ? 'Сохраняем…'
              : weeklyBudget?.paused
                ? 'Увеличить и возобновить'
                : 'Сохранить'}
          </Button>
        </div>
      </Modal>
    </>
  );
}

function AdvertisingWalletCard({
  credit,
}: {
  credit: SellerAdvertisingCredit | undefined;
}) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [walletTab, setWalletTab] = useState<string | null>('invoice');
  const [invoiceAmount, setInvoiceAmount] = useState('');
  const [payableAmount, setPayableAmount] = useState('');
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [payoutAmount, setPayoutAmount] = useState('');
  const [issued, setIssued] = useState<{
    id: number;
    number: string;
    amountRub: number;
    qrDataUrl: string;
  } | null>(null);

  const issueAndDownload = useMutation({
    mutationFn: async (amountRub: number) => {
      const invoice = await sellerFinanceApi.issueAdvertisingInvoice(amountRub);
      const blob = await sellerFinanceApi.downloadAdvertisingInvoicePdf(invoice.id);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Счет-${invoice.number}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
      return invoice;
    },
    onSuccess: (invoice) => {
      setIssued({
        id: invoice.id,
        number: invoice.number,
        amountRub: invoice.amountRub,
        qrDataUrl: invoice.qrDataUrl ?? '',
      });
    },
    onError: (e: unknown) => {
      notifications.show({
        title: 'Не удалось сформировать счёт',
        message: mutationError(e),
        color: 'red',
      });
    },
  });

  const refreshWallet = () => {
    void queryClient.invalidateQueries({ queryKey: ['seller-advertising-credit'] });
    void queryClient.invalidateQueries({
      queryKey: ['products', 'seller-promotion'],
    });
    void queryClient.invalidateQueries({ queryKey: ['seller-finance'] });
  };

  const transferFromPayable = useMutation({
    mutationFn: (amountRub: number) =>
      sellerFinanceApi.transferAdvertisingFromPayable(amountRub),
    onSuccess: () => {
      refreshWallet();
      notifications.show({
        title: 'Баланс пополнен',
        message: 'Сумма переведена с баланса к выплате',
        color: 'green',
      });
      setPayableAmount('');
    },
    onError: (e: unknown) => {
      notifications.show({
        title: 'Не удалось перевести',
        message: mutationError(e),
        color: 'red',
      });
    },
  });

  const returnToPayable = useMutation({
    mutationFn: (amountRub: number) =>
      sellerFinanceApi.returnAdvertisingToPayable(amountRub),
    onSuccess: () => {
      refreshWallet();
      notifications.show({
        title: 'Сумма выведена',
        message: 'Деньги на балансе магазина',
        color: 'green',
      });
      setWithdrawOpen(false);
      setPayoutAmount('');
    },
    onError: (e: unknown) => {
      notifications.show({
        title: 'Не удалось вывести',
        message: mutationError(e),
        color: 'red',
      });
    },
  });

  const mockPay = useMutation({
    mutationFn: (invoiceId: number) => sellerFinanceApi.mockPayAdvertisingInvoice(invoiceId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['seller-advertising-credit'] });
      void queryClient.invalidateQueries({
        queryKey: ['products', 'seller-promotion'],
      });
      notifications.show({
        title: 'Оплата зачислена',
        message: 'Тестовый платёж пополнил рекламный баланс',
        color: 'green',
      });
      setOpen(false);
      setIssued(null);
      setInvoiceAmount('');
    },
    onError: (e: unknown) => {
      notifications.show({
        title: 'Не удалось отметить оплату',
        message: mutationError(e),
        color: 'red',
      });
    },
  });

  const close = () => {
    if (
      issueAndDownload.isPending ||
      mockPay.isPending ||
      transferFromPayable.isPending ||
      returnToPayable.isPending
    ) {
      return;
    }
    setOpen(false);
    setIssued(null);
    setInvoiceAmount('');
    setPayableAmount('');
    setWithdrawOpen(false);
    setPayoutAmount('');
  };

  const closeWithdraw = () => {
    if (returnToPayable.isPending) return;
    setWithdrawOpen(false);
    setPayoutAmount('');
  };

  const loaded = credit != null;
  const payableBalanceRub = credit?.payableBalanceRub ?? 0;
  const withdrawableRub = credit?.advertisingBalanceRub ?? 0;
  const busy =
    issueAndDownload.isPending ||
    mockPay.isPending ||
    transferFromPayable.isPending ||
    returnToPayable.isPending;

  const parsedAmount = (raw: string) => {
    const value = Number(raw.replace(',', '.'));
    if (!Number.isFinite(value) || value < 1) return null;
    return value;
  };

  const submitInvoice = () => {
    const value = parsedAmount(invoiceAmount);
    if (value == null) return;
    issueAndDownload.mutate(value);
  };

  const submitTransfer = () => {
    const value = parsedAmount(payableAmount);
    if (value == null) return;
    if (value > payableBalanceRub) {
      notifications.show({
        title: 'Недостаточно средств',
        message: 'Сумма больше баланса к выплате',
        color: 'red',
      });
      return;
    }
    transferFromPayable.mutate(value);
  };

  const submitReturn = () => {
    const value = parsedAmount(payoutAmount);
    if (value == null) return;
    if (value > withdrawableRub) {
      notifications.show({
        title: 'Недостаточно средств',
        message: 'Сумма больше рекламного баланса',
        color: 'red',
      });
      return;
    }
    returnToPayable.mutate(value);
  };

  return (
    <>
      <div className="seller-promotion-wallet">
        <div className="seller-promotion-wallet__main">
          <div>
            <div className="seller-promotion-wallet__head">
              <p className="seller-promotion-budget__title">Рекламный баланс</p>
              <DotMenu
                items={[
                  {
                    label: 'Вывести',
                    onClick: () => {
                      setPayoutAmount('');
                      setWithdrawOpen(true);
                    },
                    disabled: !loaded || busy,
                  },
                  {
                    label: 'История',
                    onClick: () => setHistoryOpen(true),
                    disabled: !loaded,
                  },
                ]}
              />
            </div>
            <p className="seller-promotion-wallet__balance">
              {credit != null ? `${formatRub(credit.advertisingBalanceRub)} ₽` : '—'}
            </p>
          </div>
          <Button onClick={() => setOpen(true)} disabled={!loaded}>
            Пополнить
          </Button>
        </div>
      </div>
      <Modal
        isOpen={open}
        onClose={close}
        title={issued ? 'Счёт сформирован' : 'Рекламный баланс'}
        maxWidth={420}
      >
        {issued ? (
          <div className="seller-promotion-wallet__modal">
            <div className="seller-promotion-wallet__qr">
              {issued.qrDataUrl ? (
                <img src={issued.qrDataUrl} alt="QR для оплаты счёта" />
              ) : null}
              <p className="seller-promotion-wallet__qr-caption">
                Счёт {issued.number} на {formatRub(issued.amountRub)} ₽. Платёжка скачана.
                Отсканируйте QR в приложении любого банка — сумма и назначение уже в коде.
              </p>
            </div>
            <Button
              onClick={() => mockPay.mutate(issued.id)}
              disabled={mockPay.isPending}
            >
              {mockPay.isPending ? 'Зачисляем…' : 'Оплатил'}
            </Button>
            <p className="seller-promotion-wallet__qr-note">
              Тестовая кнопка: отмечает счёт оплаченным и пополняет баланс без банка.
            </p>
          </div>
        ) : (
          <Tabs
            value={walletTab}
            onChange={setWalletTab}
            className="seller-promotion-wallet__tabs"
          >
            <Tabs.List className="seller-promotion-tabs-list">
              <Tabs.Tab value="invoice" className="seller-promotion-tabs-tab">
                По счёту
              </Tabs.Tab>
              <Tabs.Tab value="payable" className="seller-promotion-tabs-tab">
                С баланса
              </Tabs.Tab>
            </Tabs.List>
            <Tabs.Panel value="invoice">
              <div className="seller-promotion-wallet__modal">
                <p className="seller-promotion-budget__hint">
                  Оплата счёта зачисляется на рекламный баланс.
                </p>
                <Input
                  type="number"
                  min={1}
                  label="Сумма пополнения, ₽"
                  placeholder="1000"
                  value={invoiceAmount}
                  onChange={(e) => setInvoiceAmount(e.currentTarget.value)}
                  disabled={busy}
                />
                <Button onClick={submitInvoice} disabled={busy}>
                  {issueAndDownload.isPending ? 'Формируем счет…' : 'Выставить счёт'}
                </Button>
              </div>
            </Tabs.Panel>
            <Tabs.Panel value="payable">
              <div className="seller-promotion-wallet__modal">
                <p className="seller-promotion-budget__hint">
                  Доступно к выплате: {formatRub(payableBalanceRub)} ₽.
                </p>
                <Input
                  type="number"
                  min={1}
                  label="Сумма перевода, ₽"
                  placeholder="1000"
                  value={payableAmount}
                  onChange={(e) => setPayableAmount(e.currentTarget.value)}
                  disabled={busy}
                />
                <Button
                  onClick={submitTransfer}
                  disabled={busy || payableBalanceRub < 1}
                >
                  {transferFromPayable.isPending ? 'Переводим…' : 'Перевести'}
                </Button>
              </div>
            </Tabs.Panel>
          </Tabs>
        )}
      </Modal>
      <Modal
        isOpen={withdrawOpen}
        onClose={closeWithdraw}
        title="Вывод на баланс магазина"
        maxWidth={420}
      >
        <div className="seller-promotion-wallet__modal">
          <p className="seller-promotion-budget__hint">
            {`К выводу ${formatRub(withdrawableRub)} ₽. Вся сумма, включая пополнение по счёту, уйдёт на баланс магазина.`}
          </p>
          <Input
            type="number"
            min={1}
            label="Сумма, ₽"
            placeholder="1000"
            value={payoutAmount}
            onChange={(e) => setPayoutAmount(e.currentTarget.value)}
            disabled={busy}
          />
          <Button
            onClick={submitReturn}
            disabled={busy || withdrawableRub < 1}
          >
            {returnToPayable.isPending ? 'Выводим…' : 'Вывести'}
          </Button>
        </div>
      </Modal>
      <Modal
        isOpen={historyOpen}
        onClose={() => setHistoryOpen(false)}
        title="История"
        maxWidth={720}
      >
        <OptionsTable
          columns={ledgerHistoryColumns}
          data={credit?.entries ?? []}
          emptyMessage="Проводок пока нет"
        />
      </Modal>
    </>
  );
}

function mutationError(e: unknown): string {
  if (e && typeof e === 'object' && 'response' in e) {
    const message = (e as { response?: { data?: { message?: string } } }).response?.data
      ?.message;
    if (message) return message;
  }
  return e instanceof Error ? e.message : 'Ошибка запроса';
}

export default function SellerPromotionPage() {
  const location = useLocation();
  const openedTab = (location.state as { promotionTab?: string } | null)?.promotionTab;
  const { data, isLoading, error, refetch, isFetching } = useSellerPromotion();
  const items = data?.items ?? [];
  const [showCpcInfoModal, setShowCpcInfoModal] = useState(false);
  const [activeTab, setActiveTab] = useState<string | null>(
    openedTab === 'search' ? 'search' : 'catalog',
  );
  const { data: advertisingCredit } = useQuery({
    queryKey: ['seller-advertising-credit'],
    queryFn: () => sellerFinanceApi.getMyAdvertisingCredit(),
    staleTime: 30_000,
  });
  const walletEmpty =
    advertisingCredit != null && advertisingCredit.availableForAdsRub <= 0;
  const budgetPaused = Boolean(advertisingCredit?.weeklyBudget?.paused);
  const adsBalance =
    advertisingCredit != null ? advertisingCredit.availableForAdsRub : null;
  const weeklyRemainingRub =
    advertisingCredit?.weeklyBudget?.remainingRub ?? null;
  const inputLocked = budgetPaused;
  const lockReason = budgetPaused ? 'Достигнут недельный лимит бюджета' : null;

  const promotionTableData = useMemo(
    () => items.map((row) => ({ ...row, id: row.productId })),
    [items],
  );

  const promotionColumns = useMemo<OptionsTableColumn<PromotionTableRow>[]>(
    () => [
      {
        key: 'imageUrl',
        label: 'Фото',
        style: { width: 56 },
        render: (row) => <ProductThumb imageUrl={row.imageUrl} />,
      },
      {
        key: 'name',
        label: 'Название',
        render: (row) => (
          <Link
            to={`/products/${row.productId}`}
            state={{ from: '/promotion' }}
            className="seller-promotion-table__name-link"
          >
            {row.name}
          </Link>
        ),
      },
      {
        key: 'promotionEnabled',
        label: 'Вкл.',
        style: { width: 96, textAlign: 'center' as const },
        render: (row) => (
          <PromotionToggleCell
            row={row}
            inputLocked={inputLocked}
            lockReason={lockReason}
            balanceRub={adsBalance}
            budgetPaused={budgetPaused}
            weeklyRemainingRub={weeklyRemainingRub}
          />
        ),
      },
      {
        key: 'cpcRub',
        label: 'Цена клика, ₽',
        style: { ...numericColStyle, width: 120 },
        render: (row) => (
          <div className="seller-promotion-table__cpc-cell">
            <PromotionCpcCell row={row} inputLocked={inputLocked} />
          </div>
        ),
      },
      {
        key: 'billedCpcViews',
        label: (
          <PromotionMetricHeader
            label="Платные клики"
            hint="Покупатель открыл товар с места, которое он получил благодаря платному продвижению. За такой переход списывается указанная вами цена клика."
          />
        ),
        style: numericColStyle,
        render: (row) => formatCount(row.billedCpcViews),
      },
      {
        key: 'viewedCount',
        label: (
          <PromotionMetricHeader
            label="Просмотры"
            hint="Все открытия карточки товара: из обычной бесплатной выдачи, избранного, по ссылке и из платного продвижения. Это общий счётчик — деньги списываются только за те просмотры, которые указаны в колонке «Платные клики»."
          />
        ),
        style: numericColStyle,
        render: (row) => formatCount(row.viewedCount),
      },
      {
        key: 'purchaseCount',
        label: 'Заказы',
        style: numericColStyle,
        render: (row) => formatCount(row.purchaseCount),
      },
    ],
    [inputLocked, lockReason, adsBalance, budgetPaused, weeklyRemainingRub],
  );

  return (
    <div className="page-container seller-promotion-page">
      <Breadcrumbs
        items={[{ label: 'Главная', path: '/' }, { label: 'Продвижение' }]}
      />
      <Tabs
        value={activeTab}
        onChange={setActiveTab}
        keepMounted={false}
        className="seller-promotion-tabs"
      >
        <div className="page-header">
          <div className="page-header-top seller-promotion-page-header-top">
            <div className="seller-promotion-page__title-row">
              <h1>Продвижение</h1>
              <button
                type="button"
                className="seller-promotion-page__info-btn"
                aria-label="Как работает платное продвижение в каталоге"
                onClick={() => setShowCpcInfoModal(true)}
              >
                <IoInformationCircleOutline size={20} aria-hidden />
              </button>
            </div>
            <Tabs.List className="seller-promotion-tabs-list">
              <Tabs.Tab value="catalog" className="seller-promotion-tabs-tab">
                Мои товары
              </Tabs.Tab>
              <Tabs.Tab value="search" className="seller-promotion-tabs-tab">
                Место в поиске
              </Tabs.Tab>
            </Tabs.List>
          </div>
          {activeTab === 'search' ? (
            <p className="seller-promotion-page__lead">
              На место в выдче влияют влияют ставка, число
              заказов за 30 дней и рейтинг, так же дополнительный буст получают новые товары. Реальное место у покупателя отчасти зависит от близости магазина к покупателю и может отличаться от таблицы.
            </p>
          ) : (
            <p className="seller-promotion-page__lead">
              Ставка учитывается в выдаче города покупателя после пересчёта — обычно в
              течение 10 минут. Менять ставку или включать/выключать продвижение можно
              не чаще одного раза в 15 минут.
              {advertisingCredit
                ? ` Ставка от ${formatRub(advertisingCredit.cpcMinBidRub)} до ${formatRub(advertisingCredit.cpcMaxBidRub)} ₽.`
                : ''}
            </p>
          )}
          {activeTab === 'catalog' ? (
            <>
              <div className="seller-promotion-finance-row">
                <AdvertisingWalletCard credit={advertisingCredit} />
                <WeeklyBudgetCard weeklyBudget={advertisingCredit?.weeklyBudget} />
              </div>
              {walletEmpty ? (
                <Alert tone="error" mt="sm" title="Рекламный баланс пуст">
                  Клики не списываются, пока на балансе нет средств. Товары и ставки
                  сохраняются. Пополните баланс по счёту или переводом с баланса
                  магазина — продвижение продолжится.
                </Alert>
              ) : null}
              {budgetPaused ? (
                <Alert tone="error" mt="sm" title="Лимит бюджета достигнут">
                  Недельный бюджет на продвижение исчерпан. Включённые товары и ставки
                  сохранены. Увеличьте бюджет кнопкой «Изменить», чтобы возобновить
                  продвижение в том же составе, либо дождитесь новой недели.
                </Alert>
              ) : null}
            </>
          ) : null}
        </div>
        <Tabs.Panel value="catalog" className="seller-promotion-tabs-panel">
          <div className="content-wrapper seller-promotion-tab-body">
            <div className="seller-promotion-scroll">
              <div className="seller-promotion-desktop-table">
                <OptionsTable
                  columns={promotionColumns}
                  data={promotionTableData}
                  loading={isLoading || isFetching}
                  error={error}
                  refetch={refetch}
                  emptyMessage="Нет товаров"
                />
              </div>

              <div
                className="seller-promotion-mobile-list"
                aria-label="Список товаров для продвижения"
              >
                {error && refetch ? (
                  <RefetchButton
                    refetch={refetch}
                    isLoading={isLoading || isFetching}
                    errorMessage={
                      error instanceof Error
                        ? error.message
                        : 'Не удалось загрузить данные'
                    }
                  />
                ) : isLoading || isFetching ? (
                  <div className="empty-container">
                    <Loader />
                  </div>
                ) : promotionTableData.length === 0 ? (
                  <div className="empty-container">
                    <p>Нет товаров</p>
                  </div>
                ) : (
                  promotionTableData.map((row) => (
                    <article
                      key={row.productId}
                      className="seller-promotion-mobile-card"
                    >
                      <div className="seller-promotion-mobile-card__top">
                        <ProductThumb imageUrl={row.imageUrl} />
                        <div className="seller-promotion-mobile-card__main">
                          <Link
                            to={`/products/${row.productId}`}
                            state={{ from: '/promotion' }}
                            className="seller-promotion-mobile-card__name"
                          >
                            {row.name}
                          </Link>
                          <div className="seller-promotion-mobile-card__controls">
                            <div className="seller-promotion-mobile-card__control">
                              <span className="seller-promotion-mobile-card__label">
                                Вкл.
                              </span>
                              <PromotionToggleCell
                                row={row}
                                inputLocked={inputLocked}
                                lockReason={lockReason}
                                balanceRub={adsBalance}
                                budgetPaused={budgetPaused}
                                weeklyRemainingRub={weeklyRemainingRub}
                              />
                            </div>
                            <div className="seller-promotion-mobile-card__control">
                              <span className="seller-promotion-mobile-card__label">
                                Цена клика, ₽
                              </span>
                              <PromotionCpcCell
                                row={row}
                                inputLocked={inputLocked}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="seller-promotion-mobile-card__metrics">
                        <div className="seller-promotion-mobile-card__metric">
                          <span className="seller-promotion-mobile-card__metric-label">
                            Платные клики
                          </span>
                          <span className="seller-promotion-mobile-card__metric-value">
                            {formatCount(row.billedCpcViews)}
                          </span>
                        </div>
                        <div className="seller-promotion-mobile-card__metric">
                          <span className="seller-promotion-mobile-card__metric-label">
                            Просмотры
                          </span>
                          <span className="seller-promotion-mobile-card__metric-value">
                            {formatCount(row.viewedCount)}
                          </span>
                        </div>
                        <div className="seller-promotion-mobile-card__metric">
                          <span className="seller-promotion-mobile-card__metric-label">
                            Заказы
                          </span>
                          <span className="seller-promotion-mobile-card__metric-value">
                            {formatCount(row.purchaseCount)}
                          </span>
                        </div>
                      </div>
                    </article>
                  ))
                )}
              </div>
            </div>
          </div>
        </Tabs.Panel>
        <Tabs.Panel
          value="search"
          className="seller-promotion-tabs-panel seller-promotion-tabs-panel--search"
        >
          <SellerSearchPreviewTab />
        </Tabs.Panel>
      </Tabs>

      <Modal
        isOpen={showCpcInfoModal}
        onClose={() => setShowCpcInfoModal(false)}
        title="Как работает продвижение"
        maxWidth={680}
        classNames={{
          content: 'seller-promotion-info-modal-dialog',
          body: 'seller-promotion-info-modal-dialog__body',
        }}
      >
        <div className="seller-promotion-info-modal">
          <p className="seller-promotion-info-modal__hint">
            Вы назначаете ставку за клик по карточке товара — в рублях. Чем она выше,
            тем выше шансы попасть в верхнюю часть выдачи в городе покупателя
            (категория, главная, поиск).
            {advertisingCredit
              ? ` Ставка от ${formatRub(advertisingCredit.cpcMinBidRub)} до ${formatRub(advertisingCredit.cpcMaxBidRub)} ₽.`
              : ''}
          </p>

          <div className="seller-promotion-info-modal__card">
            <p className="seller-promotion-info-modal__steps-title">Чтобы начать</p>
            <ol className="seller-promotion-info-modal__steps">
              <li>
                Пополните рекламный баланс: по счёту или переводом с баланса магазина.
              </li>
              <li>Укажите недельный лимит, если нужно ограничить расход.</li>
              <li>Укажите цену клика в таблице.</li>
              <li>Включите тумблер «Вкл.»</li>
              <li>
                После пересчёта ранга (обычно до 10 минут) ставка попадёт в выдачу
                вашего города, и платформа начнёт списывать деньги за клики.
              </li>
            </ol>
          </div>

          <div className="seller-promotion-info-modal__notice" role="note">
            <p className="seller-promotion-info-modal__notice-title">Важно</p>
            <p className="seller-promotion-info-modal__notice-text">
              Ставку и тумблер можно менять не чаще раза в 15 минут. Клики списываются
              с рекламного баланса. Если на ставку товара не хватает баланса или остатка
              недельного бюджета, продвижение этого товара останавливается: клик не
              списывается и ставка не учитывается в выдаче. Товары, на которые денег
              хватает, продолжают продвигаться. Вывести рекламный баланс можно только
              на баланс магазина.
            </p>
          </div>

          <div className="seller-promotion-info-modal__footer">
            <a
              href={sellerHelpUrl('cpc-money')}
              className="seller-promotion-info-modal__help-link"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setShowCpcInfoModal(false)}
            >
              Подробнее в «Помощи»
            </a>
            <Button onClick={() => setShowCpcInfoModal(false)}>Понятно</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
